import React, { useState, useRef, useEffect } from 'react';
import { RefreshCw, Move, X, ChevronDown } from 'lucide-react';

interface ATRItem {
  id: number;
  asset: string;
  completed: boolean;
  equipment?: {
    description?: string;
    serial_number?: string;
    model_number?: string;
  };
  descriptions?: Array<{
    id: number;
    description: string;
  }>;
}

interface ATRItemsBoxProps {
  atrItems: ATRItem[];
  isLoading: boolean;
  onSync?: () => void;
  isSyncing?: boolean;
  onComplete?: (id: number) => void;
  showCompleteActions?: boolean;
  onEditSerialNumber?: (equipment: ATRItem['equipment']) => void;
  draggable?: boolean;
  defaultPosition?: 'bottom-left' | 'bottom-right';
}

const ATRItemsBox: React.FC<ATRItemsBoxProps> = ({
  atrItems,
  isLoading,
  onSync,
  isSyncing,
  onComplete,
  showCompleteActions = true,
  onEditSerialNumber,
  draggable = false,
  defaultPosition = 'bottom-left',
}) => {
  const [position, setPosition] = useState(defaultPosition);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isCollapsed, setIsCollapsed] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  
  // Detect if device is mobile
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
  
  // Calculate pending items count
  const pendingItemsCount = atrItems.filter(item => !item.completed).length;
  
  // Determine background color based on items status
  const getBackgroundColor = () => {
    if (atrItems.length === 0) return 'bg-gray-100 dark:bg-gray-800'; // No items - neutral
    if (pendingItemsCount > 0) return 'bg-yellow-100 dark:bg-yellow-900'; // Pending items - warning
    return 'bg-green-100 dark:bg-green-900'; // All completed - success
  };
  
  const getBorderColor = () => {
    if (atrItems.length === 0) return 'border-gray-300 dark:border-gray-600'; // No items - neutral
    if (pendingItemsCount > 0) return 'border-yellow-300 dark:border-yellow-600'; // Pending items - warning
    return 'border-green-300 dark:border-green-600'; // All completed - success
  };
  
  // Don't render if no items at all
  if (atrItems.length === 0 && !isLoading) {
    return null;
  }

  // Handle drag start (mouse)
  const handleMouseDown = (e: React.MouseEvent) => {
    if (!draggable || isMobile) return;
    
    setIsDragging(true);
    const rect = boxRef.current?.getBoundingClientRect();
    if (rect) {
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
    }
  };

  // Handle drag start (touch)
  const handleTouchStart = (e: React.TouchEvent) => {
    if (!draggable || !isMobile) return;
    
    setIsDragging(true);
    const rect = boxRef.current?.getBoundingClientRect();
    if (rect && e.touches[0]) {
      setDragOffset({
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top
      });
    }
  };

  // Handle drag move
  useEffect(() => {
    if (!isDragging || !draggable) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!boxRef.current || isMobile) return;

      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;

      // Constrain to viewport bounds
      const maxX = window.innerWidth - boxRef.current.offsetWidth;
      const maxY = window.innerHeight - boxRef.current.offsetHeight;

      const constrainedX = Math.max(0, Math.min(newX, maxX));
      const constrainedY = Math.max(0, Math.min(newY, maxY));

      boxRef.current.style.left = `${constrainedX}px`;
      boxRef.current.style.top = `${constrainedY}px`;
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!boxRef.current || !isMobile) return;

      // Prevent page scrolling during drag (only if event is cancelable)
      if (e.cancelable) {
        e.preventDefault();
      }

      if (e.touches[0]) {
        const newX = e.touches[0].clientX - dragOffset.x;
        const newY = e.touches[0].clientY - dragOffset.y;

        // Constrain to viewport bounds
        const maxX = window.innerWidth - boxRef.current.offsetWidth;
        const maxY = window.innerHeight - boxRef.current.offsetHeight;

        const constrainedX = Math.max(0, Math.min(newX, maxX));
        const constrainedY = Math.max(0, Math.min(newY, maxY));

        boxRef.current.style.left = `${constrainedX}px`;
        boxRef.current.style.top = `${constrainedY}px`;
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    const handleTouchEnd = () => {
      setIsDragging(false);
    };

    if (isMobile) {
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleTouchEnd, { passive: true });
    } else {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      if (isMobile) {
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
      } else {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      }
    };
  }, [isDragging, dragOffset, draggable, isMobile]);

  // Quick position buttons
  const moveToPosition = (pos: 'bottom-left' | 'bottom-right') => {
    if (!boxRef.current) return;
    
    setPosition(pos);
    const rect = boxRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    if (pos === 'bottom-left') {
      boxRef.current.style.left = '20px';
      boxRef.current.style.top = `${viewportHeight - rect.height - 20}px`;
    } else {
      boxRef.current.style.left = `${viewportWidth - rect.width - 20}px`;
      boxRef.current.style.top = `${viewportHeight - rect.height - 20}px`;
    }
  };

  return (
          <div
        ref={boxRef}
        className={`${draggable ? 'fixed z-50' : ''}`}
        style={draggable ? {
          left: isMobile ? '10px' : (position === 'bottom-left' ? '20px' : 'auto'),
          right: isMobile ? '10px' : (position === 'bottom-right' ? '20px' : 'auto'),
          bottom: '20px',
          width: isMobile ? 'auto' : (isCollapsed ? '200px' : '400px'),
          minWidth: isMobile ? 'auto' : (isCollapsed ? '200px' : '400px'),
          minHeight: isCollapsed ? '60px' : '300px',
          maxWidth: isMobile ? 'calc(100vw - 20px)' : '90vw',
          maxHeight: '80vh',
          overflowY: 'auto',
          transition: 'width 0.3s ease, min-height 0.3s ease'
        } : {}}
      >
      <section 
        className={`${isCollapsed ? 'space-y-0' : 'space-y-6'} ${getBackgroundColor()} rounded-lg shadow-lg border-2 ${getBorderColor()} ${isCollapsed ? 'p-3' : 'p-6'} ${draggable ? (isMobile ? 'cursor-default' : 'cursor-move') : ''}`}
        onMouseDown={draggable && !isMobile ? handleMouseDown : undefined}
        onTouchStart={draggable && isMobile ? handleTouchStart : undefined}
      >
        <div className={`flex ${isCollapsed ? 'justify-center' : 'justify-between'} items-center`}>
          <div className="flex items-center gap-2">
            {!isCollapsed && (
              <>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">ATR Items</h2>
                {draggable && (
                  <Move size={16} className="text-gray-400" />
                )}
              </>
            )}
            {isCollapsed && (
              <h2 className="text-sm font-bold text-gray-900 dark:text-white">
                {pendingItemsCount > 0 ? `${pendingItemsCount}` : '✓'}
              </h2>
            )}
          </div>
          {!isCollapsed && (
            <div className="flex items-center gap-2">
              {draggable && !isMobile && (
                <div className="flex gap-1">
                  <button
                    onClick={() => moveToPosition('bottom-left')}
                    className={`px-2 py-1 text-xs rounded ${position === 'bottom-left' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300'}`}
                    title="Move to Bottom Left"
                  >
                    BL
                  </button>
                  <button
                    onClick={() => moveToPosition('bottom-right')}
                    className={`px-2 py-1 text-xs rounded ${position === 'bottom-right' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300'}`}
                    title="Move to Bottom Right"
                  >
                    BR
                  </button>
                </div>
              )}
              {onSync && (
                <button
                  onClick={onSync}
                  disabled={isSyncing}
                  className={`flex items-center gap-2 px-4 py-2 text-white bg-blue-600 dark:bg-blue-500 rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors duration-200 ${isSyncing ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <RefreshCw size={20} className={isSyncing ? 'animate-spin' : ''} />
                  <span>{isSyncing ? 'Syncing...' : 'Sync ATR Items'}</span>
                </button>
              )}
            </div>
          )}
          {draggable && (
            <button
              onClick={() => {
                const newCollapsed = !isCollapsed;
                setIsCollapsed(newCollapsed);
                
                // When collapsing, move to bottom-left and make button-sized
                if (newCollapsed && boxRef.current) {
                  // Force absolute positioning to bottom-left corner
                  boxRef.current.style.left = '20px';
                  boxRef.current.style.right = 'auto';
                  boxRef.current.style.top = 'auto';
                  boxRef.current.style.bottom = '20px';
                  setPosition('bottom-left');
                }
              }}
              className="p-1.5 rounded-md border border-gray-300 dark:border-gray-600 hover:border-red-500 dark:hover:border-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors duration-200"
              title={isCollapsed ? 'Expand' : 'Collapse'}
            >
              {isCollapsed ? (
                <ChevronDown size={16} className="text-gray-400 hover:text-red-600 dark:hover:text-red-400" />
              ) : (
                <X size={16} className="text-gray-400 hover:text-red-600 dark:hover:text-red-400" />
              )}
            </button>
          )}
        </div>
      {!isCollapsed && (
        <>
          <p className="text-sm text-gray-600 dark:text-gray-400">Please mark all finished work as <span className="font-semibold">Completed</span> ✅</p>
          <div className="grid gap-4">
        {isLoading ? (
          <div className="text-center py-4 bg-white dark:bg-gray-800 rounded-lg shadow-md">
            Loading ATR items...
          </div>
        ) : atrItems.length > 0 ? (
          atrItems.map(item => (
            <div
              key={item.id}
              className={`p-4 bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700`}
            >
              <div className="flex justify-between items-center">
                <div className="flex-1">
                  <p className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                    {item.asset}
                    {item.equipment && onEditSerialNumber && (
                      <button
                        type="button"
                        className="ml-1 p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
                        title="Edit Serial Number"
                        onClick={() => onEditSerialNumber(item.equipment)}
                        style={{ lineHeight: 0 }}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                          <path d="M15.232 5.232l3.536 3.536M9 11l6 6M3 21h6l11-11a2.828 2.828 0 0 0-4-4L5 17v4z" />
                        </svg>
                      </button>
                    )}
                  </p>
                  {item.equipment && item.equipment.model_number && !item.asset.includes(item.equipment.model_number) && (
                    <div className="mt-2 text-sm">
                      <p className="text-gray-700 dark:text-gray-300">
                        <span className="font-medium">Model:</span> {item.equipment.model_number}
                      </p>
                    </div>
                  )}
                  {item.descriptions && item.descriptions.length > 0 && (
                    <div className="mt-2 text-sm">
                      <p className="font-medium text-gray-700 dark:text-gray-300">Repair Descriptions:</p>
                      <ul className="list-disc list-inside mt-1 space-y-1">
                        {item.descriptions.map((desc) => (
                          <li key={desc.id} className="text-gray-700 dark:text-gray-300">
                            {desc.description}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                <div className="flex items-center">
                  {item.completed ? (
                    <span className="px-2 py-1 text-xs font-semibold text-green-700 bg-green-100 rounded-full dark:bg-green-700 dark:text-green-100">
                      Completed
                    </span>
                  ) : showCompleteActions && onComplete ? (
                    <div className="flex flex-col items-center gap-1">
                      <button
                        onClick={() => onComplete(item.id)}
                        className="p-1.5 rounded-md border border-gray-300 dark:border-gray-600 hover:border-indigo-500 dark:hover:border-indigo-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
                        title="Mark as Completed"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400">
                          <path d="M20 6 9 17l-5-5" />
                        </svg>
                      </button>
                      <span className="px-2 py-1 text-xs font-semibold text-yellow-800 bg-yellow-100 rounded-full dark:bg-yellow-700 dark:text-yellow-100 mt-1">
                        Pending
                      </span>
                    </div>
                  ) : (
                    <span className="px-2 py-1 text-xs font-semibold text-yellow-800 bg-yellow-100 rounded-full dark:bg-yellow-700 dark:text-yellow-100">
                      Pending
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-4 bg-white text-gray-400 dark:bg-gray-800 rounded-lg shadow-md">
            No ATR items found
          </div>
        )}
          </div>
        </>
      )}
    </section>
    </div>
  );
};

export default ATRItemsBox; 